import { MultiSelectFilter, type MultiSelectOption } from '../../components/ui/MultiSelectFilter';
import { SelectField } from '../../components/ui/SelectField';
import { formatUserDisplayName } from '../../lib/formatUserDisplayName';
import {
  ASSET_PRESENCE_OPTIONS,
  BUILD_STATUS_OPTIONS,
  type AssetPresenceFilter,
  type BuildStatusFilter,
} from '../../lib/assetBuildFilters';
import type { CurrentUser } from '../../types/user';
import type { Campaign } from '../../types/campaign';

// Filter bar (spec §7.1). All filters stack. AM, Campaign, and Group are
// multi-select with 250ms debounce (extracted as <MultiSelectFilter>). Status
// stays single-select because its buckets are already a roll-up.
//
// The "me" AM convenience ("show only rows assigned to me") is modelled as a
// sentinel value ('__me__') inside `am[]`; MasterGrid resolves it to the
// signed-in user's uid at filter-apply time.

export type StatusFilter = 'all' | 'drafting' | 'ready' | 'building' | 'live' | 'complete' | 'cancelled';

// Sentinel group value for Independent locations (parent = brand).
export const NO_GROUP = '__no_group__';

// Sentinel campaign value for rows where campaignId is null (rowType='unassigned').
export const UNASSIGNED_CAMPAIGN = '__unassigned__';

// Sentinel AM value representing "signed-in user" -- resolved to current uid
// downstream. Coexists with real uids in the `am[]` array.
export const ME = '__me__';

export type Filters = {
  am: string[];           // uids plus optionally ME
  status: StatusFilter;   // single-select, stays scalar
  campaignIds: string[];  // campaign ids plus optionally UNASSIGNED_CAMPAIGN
  groups: string[];       // group names plus optionally NO_GROUP
  text: string;
  // Multi Lead Form spec §3.3: convenience toggle for rows whose leadFormId
  // points to an entry that no longer exists in the assigned Campaign's
  // library. Independent from the status enum (orphan can co-exist with
  // any lifecycle status); rendered as a tickbox in the filter bar.
  leadFormOrphan: boolean;
  // Organic Assets filter additions. Multi-select; OR within, AND across.
  assetPresence: AssetPresenceFilter[];
  buildStatus: BuildStatusFilter[];
};

export const DEFAULT_FILTERS: Filters = {
  am: [],
  status: 'all',
  campaignIds: [],
  groups: [],
  text: '',
  leadFormOrphan: false,
  assetPresence: [],
  buildStatus: [],
};

type Props = {
  filters: Filters;
  onChange: (next: Filters) => void;
  users: CurrentUser[];
  campaignsInRollout: Campaign[];
  groupsInRollout: string[];      // distinct group names, alpha-sorted
  hasIndependentLocations: boolean;
  currentUser: CurrentUser;
};

export function FilterBar({
  filters,
  onChange,
  users,
  campaignsInRollout,
  groupsInRollout,
  hasIndependentLocations,
  currentUser,
}: Props) {
  const amOptions: MultiSelectOption[] = [
    { value: ME, label: `Me (${formatUserDisplayName(currentUser)})` },
    ...users.map((u) => ({ value: u.uid, label: formatUserDisplayName(u) })),
  ];

  const campaignOptions: MultiSelectOption[] = [
    ...campaignsInRollout.map((c) => ({
      value: c.id,
      label: `${c.name}${c.isAlternate ? ' (Alt)' : c.isOptOut ? ' (Opt Out)' : ''}`,
    })),
    { value: UNASSIGNED_CAMPAIGN, label: '— Unassigned —' },
  ];

  // "(no group)" pinned to the top of the Groups dropdown when Independent
  // locations exist, so AMs can reach it without scrolling past every group.
  const groupOptions: MultiSelectOption[] = [
    ...(hasIndependentLocations
      ? [{ value: NO_GROUP, label: '(no group)' }]
      : []),
    ...groupsInRollout.map((g) => ({ value: g, label: g })),
  ];

  const anyActive =
    filters.am.length > 0 ||
    filters.status !== 'all' ||
    filters.campaignIds.length > 0 ||
    filters.groups.length > 0 ||
    filters.text.length > 0 ||
    filters.leadFormOrphan ||
    filters.assetPresence.length > 0 ||
    filters.buildStatus.length > 0;

  return (
    <div className="flex flex-wrap items-center gap-2 mb-4">
      <MultiSelectFilter
        label="AMs"
        options={amOptions}
        selected={filters.am}
        onChange={(am) => onChange({ ...filters, am })}
        ariaLabel="Filter by account manager"
      />

      <MultiSelectFilter
        label="Groups"
        options={groupOptions}
        selected={filters.groups}
        onChange={(groups) => onChange({ ...filters, groups })}
        ariaLabel="Filter by group"
      />

      <SelectField
        size="sm"
        aria-label="Filter by status"
        value={filters.status}
        onValueChange={(v) => onChange({ ...filters, status: v as StatusFilter })}
        options={[
          { value: 'all', label: 'All statuses' },
          { value: 'drafting', label: 'Drafting' },
          { value: 'ready', label: 'Ready' },
          { value: 'building', label: 'Building' },
          { value: 'live', label: 'Live' },
          { value: 'complete', label: 'Complete' },
          { value: 'cancelled', label: 'Cancelled' },
        ]}
      />

      <MultiSelectFilter
        label="Campaigns"
        options={campaignOptions}
        selected={filters.campaignIds}
        onChange={(campaignIds) => onChange({ ...filters, campaignIds })}
        ariaLabel="Filter by Campaign"
      />

      {/* Organic Assets filter: folder presence (Local / Organic). */}
      <MultiSelectFilter
        label="Assets"
        options={ASSET_PRESENCE_OPTIONS as MultiSelectOption[]}
        selected={filters.assetPresence}
        onChange={(vals) =>
          onChange({ ...filters, assetPresence: vals as AssetPresenceFilter[] })
        }
        ariaLabel="Filter by asset folder presence"
      />

      {/* Build status: Main / Local / Organic "not built" checkboxes. */}
      <MultiSelectFilter
        label="Build status"
        options={BUILD_STATUS_OPTIONS as MultiSelectOption[]}
        selected={filters.buildStatus}
        onChange={(vals) =>
          onChange({ ...filters, buildStatus: vals as BuildStatusFilter[] })
        }
        ariaLabel="Filter by build status"
      />

      <input
        type="search"
        value={filters.text}
        onChange={(e) => onChange({ ...filters, text: e.target.value })}
        placeholder="Search locations…"
        className="flex-1 min-w-[200px] rounded border border-slate-300 px-3 py-1.5 text-sm"
      />

      {/* Multi Lead Form spec §3.3. Surfaces only rows where the row's
          leadFormId no longer exists on the assigned Campaign — the AM's
          to-do list for reassigns. Pinned next to the search input so
          it sits in the corner of the filter row. */}
      <label className="flex items-center gap-1.5 text-xs text-slate-700 select-none cursor-pointer">
        <input
          type="checkbox"
          checked={filters.leadFormOrphan}
          onChange={(e) => onChange({ ...filters, leadFormOrphan: e.target.checked })}
        />
        Lead form needs reassign
      </label>

      {anyActive && (
        <button
          type="button"
          onClick={() => onChange(DEFAULT_FILTERS)}
          className="text-xs text-slate-500 hover:text-slate-800 underline"
        >
          Clear filters
        </button>
      )}
    </div>
  );
}
